import {
  type DrawingCanvasShape,
  type DrawingCanvasShapeStyleBase,
  DrawingCanvasShapeType,
  type Point,
  type RGBColor,
} from '../types';
import { defaultLineJoin, defaultLineWidth, defaultStrokeColor, hitRegionBuffer, strokeHighlight } from './common';

const tracePolygon = (points: Point[], ctx: CanvasRenderingContext2D): void => {
  ctx.moveTo(points[0].x, points[0].y);
  for (let i = 1; i < points.length; i++) {
    ctx.lineTo(points[i].x, points[i].y);
  }
  ctx.closePath();
};

export const renderPolygon = (
  points: Point[],
  ctx: CanvasRenderingContext2D,
  style?: DrawingCanvasShapeStyleBase,
): void => {
  if (points.length < 2) return;
  ctx.lineJoin = defaultLineJoin;
  if (style?.isHighlighted) {
    // Clips the highlight to outside the polygon so it doesn't tint the translucent fill
    ctx.save();
    ctx.beginPath();
    ctx.rect(0, 0, ctx.canvas.width, ctx.canvas.height);
    tracePolygon(points, ctx);
    ctx.clip('evenodd');
    ctx.beginPath();
    tracePolygon(points, ctx);
    strokeHighlight(ctx, style);
    ctx.restore();
  }
  ctx.lineWidth = style?.lineWidth ?? defaultLineWidth;
  ctx.strokeStyle = style?.strokeColor || defaultStrokeColor(ctx);
  ctx.fillStyle = style?.fillColor || 'rgba(0,0,0,0.2)';
  ctx.beginPath();
  tracePolygon(points, ctx);
  if (style?.lineWidth !== 0) {
    ctx.stroke();
  }
  ctx.fill();
};

export const renderPolygonHitRegion = (
  points: Point[],
  ctx: CanvasRenderingContext2D,
  color: RGBColor,
  isSolid: boolean = true,
  originalStyle?: DrawingCanvasShapeStyleBase,
): void => {
  if (points.length < 2) return;
  ctx.beginPath();
  tracePolygon(points, ctx);
  // A solid polygon is only filled, since a stroke over the fill blends off-color pixels into its hit region
  if (isSolid) {
    ctx.fillStyle = color;
    ctx.fill();
  } else {
    ctx.lineWidth = (originalStyle?.lineWidth ?? defaultLineWidth) + hitRegionBuffer;
    ctx.lineJoin = defaultLineJoin;
    ctx.strokeStyle = color;
    ctx.stroke();
  }
};

export const isValidPolygon = (drawingShape: Pick<DrawingCanvasShape, 'type' | 'points'>): boolean => {
  return drawingShape.type === DrawingCanvasShapeType.POLYGON && drawingShape.points.length >= 3;
};
